import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import LockIcon from 'src/assets/images/icons/lock-icon.png';
import { AssignmentFeedback } from 'src/components/assignments/assignment-feedback';
import { DomainBadge } from 'src/components/common/domain-badge';
import { K5CardImage } from 'src/components/elementary-school-view/k5-card-image';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useFeedback } from 'src/hooks/use-feedback.hook';
import { useFormatAssignmentDueDate } from 'src/hooks/use-format-assignment-due-date';
import { useScore } from 'src/hooks/use-score.hook';
import {
  AssignmentsViewOptions,
  DesmosAssignment,
  MSAssignmentsStatusOptions,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import { useCurrentGradeSelector } from 'src/store/features';
import {
  getAssignmentLessonNumber,
  getAssignmentLessonTitle,
  isDesmosAssignmentContent,
} from 'src/utils/assignments.utils';
import { getTheme } from 'src/utils/theme.utils';

type LowerGradeCardContentProps = {
  assignment: SingleAssignment | TaughtLessonAssignment | DesmosAssignment;
  thumbnailPath?: string;
  isThumbnailDistorted?: boolean;
  isFullyCustom?: boolean;
  isCustomized?: boolean;
  domain?: string;
  subject?: string;
  title?: string;
  isTaughtLesson?: boolean;
  dueDate?: number | null;
  isCardRemovalAnimationInProgress?: boolean;
  isStatusChangeDialogVisible?: boolean;
  section?: AssignmentsViewOptions.TODO | AssignmentsViewOptions.PAST_WORK;
};

export const LowerGradeCardContent = ({
  thumbnailPath,
  isThumbnailDistorted,
  isFullyCustom,
  isCustomized,
  domain,
  subject,
  title,
  isTaughtLesson,
  dueDate,
  isCardRemovalAnimationInProgress,
  isStatusChangeDialogVisible,
  assignment,
  section,
}: LowerGradeCardContentProps) => {
  const { t } = useTranslation();

  const formatAssignmentDueDate = useFormatAssignmentDueDate();
  const currentGrade = useCurrentGradeSelector();
  const theme = getTheme(currentGrade, false);

  const { content, isHandedInmCLASSAssessment } = assignment;
  const lessonKey = 'lessonKey' in content ? content.lessonKey : undefined;

  const lessonKeyToSearchInsight = !isTaughtLesson ? lessonKey : undefined;

  const { feedback } = useFeedback(
    assignment.contentBk,
    lessonKeyToSearchInsight,
  );
  const { score } = useScore(assignment.contentBk, lessonKeyToSearchInsight);

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const isPastWorkSectionActive = section === AssignmentsViewOptions.PAST_WORK;
  const isToDoSectionActive = section === AssignmentsViewOptions.TODO;

  const isDesmos = isDesmosAssignmentContent(content);

  const curriculumAssignmentName = isCustomized
    ? getAssignmentLessonTitle(title)
    : getAssignmentLessonNumber(title);

  return (
    <>
      <div className="assignment__thumbnail-wrapper">
        <K5CardImage
          theme={theme}
          imagePath={thumbnailPath}
          classNames={{
            containerClassnames: 'assignment__thumbnail-container',
            imageClassnames: clsx('assignment__thumbnail', {
              'assignment__thumbnail--distorted':
                thumbnailPath && isThumbnailDistorted,
            }),
          }}
          withCheckmark={
            isCardRemovalAnimationInProgress && isToDoSectionActive
          }
        />
        {isHandedInmCLASSAssessment && <img src={LockIcon} alt="lock-icon" />}
      </div>
      <h2
        className={clsx('assignment__name', 'assignment__name--young', {
          'assignment__name--young-past-work':
            isPastWorkSectionActive && !isTaughtLesson && dueDate,
          'assignment__name--hidden-to-do':
            isStatusChangeDialogVisible && isToDoSectionActive,
        })}
      >
        <span>
          {!isFullyCustom && (
            <span
              className="assignment__domain"
              data-testid="assignment-domain"
            >
              <DomainBadge domain={domain} subject={subject} />
            </span>
          )}
          {isDesmos ? title : curriculumAssignmentName}
        </span>
        {isPastWorkSectionActive && !isTaughtLesson && dueDate && (
          <span className="assignment__date assignment__date--theme-k1">
            <span className="assignment__date-type assignment__date-type--theme-k1">
              {`${t('Due')}:`}
            </span>
            <span>{formatAssignmentDueDate(new Date(dueDate))}</span>
          </span>
        )}
        <div className="assignment__feedback">
          <AssignmentFeedback
            feedback={feedback}
            score={score}
            assignment={assignment}
            commonXapiContextPayload={commonXapiContextPayload}
            assignmentStatus={MSAssignmentsStatusOptions.STARTED}
          />
        </div>
      </h2>
    </>
  );
};
